import Close from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useWindowWidth } from '@react-hook/window-size';
import { useMutation } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import isEmail from 'validator/lib/isEmail';
import { AnimateIn } from '../../components/animate-in';
import { CardIcon } from '../../components/card-icon';
import {
  CustomerData,
  CustomerPaymentData,
  EventRegistrationData,
  EventTicketData,
  TicketData,
} from '../../types';
import { signIn } from '../../utils/authenticate';
import { captureError } from '../../utils/capture-error';
import { toCurrency } from '../../utils/formatter';
import { RegisterConfirmation } from './register-confirmation';
import { RegisterPaymentAdd } from './register-payment-add';
import { RegisterSelections } from './register-selections';
import { RegisterTermsAccept } from './register-terms-accept';
import { RegisterTicketSelect } from './register-ticket-select';
import { getSelectionStatus } from './utils';
import { getErrorMessage } from '../../utils/error-message';

export function RegisterGuest(props: { data: EventRegistrationData }) {
  // State
  const [open, setOpen] = React.useState(true);
  const [activeStep, setActiveStep] = React.useState(0);
  const [register, setRegister] = React.useState({
    customerProfileId: '',
    customerPaymentProfileId: '',
    TicketTypeId: 0,
    ticketCount: '1',
    OwnerFirstName: '',
    OwnerLastName: '',
    OwnerEmail: '',
  });
  const [shirtSizes, setShirtSizes] = React.useState<{ [key: number]: string }>(
    {},
  );
  const [mealTypes, setMealTypes] = React.useState<{ [key: number]: string }>(
    {},
  );
  const [assignment, setAssignment] = React.useState({
    AttendeeTypeId: 'Guest',
    DisplayFirstName: '',
    DisplayLastName: '',
    DisplayEmail: '',
  });
  const [ticket, setTicket] = React.useState<EventTicketData>();
  const [hasAgreed, setHasAgreed] = React.useState(false);
  const [billing, setBilling] = React.useState<CustomerData>();
  const [needPurchaseInfo, setNeedPurchaseInfo] = React.useState(false);
  // Hooks
  const width = useWindowWidth();

  const handleScroll = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Mutation - Purchase Tickets
  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...register,
        EventId: props.data.EventId,
        ShirtSizes: Object.values(shirtSizes),
        MealTypes: Object.values(mealTypes),
        ticketAssignments: [assignment],
      };
      // Purchaser and Attendee are the same person
      if (!needPurchaseInfo) {
        body.OwnerFirstName = assignment.DisplayFirstName;
        body.OwnerLastName = assignment.DisplayLastName;
        body.OwnerEmail = assignment.DisplayEmail;
      }
      const response: {
        data: TicketData[];
      } = await API.post('EventsAPI', '/events/tickets/purchase/non-agent', {
        body,
      });

      return response.data;
    },
    onSuccess: () => {
      handleScroll();
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleSignIn = async () => {
    try {
      await signIn({ window });
    } catch (error) {
      captureError({ data: { error } });
    }
  };

  const ticketIsFree: boolean = Boolean(
    ticket && ticket.Price !== null && ticket.Price === 0,
  );

  let paymentProfile: CustomerPaymentData | undefined = undefined;
  if (billing && billing.customerPaymentProfiles.length) {
    paymentProfile = billing.customerPaymentProfiles.find(
      (i) => i.customerPaymentProfileId === register.customerPaymentProfileId,
    );
  }

  let payTodayMax = 0;
  if (ticket && register.ticketCount) {
    payTodayMax = ticket.Price
      ? ticket.Price * Number(register.ticketCount)
      : 0;
  }

  // Checks to see if the user has selected a ticket type that
  // needs additional selections
  const { needsToSelectShirts, needsToSelectMeals } = getSelectionStatus({
    ticket,
    shirtSizes,
    mealTypes,
    ticketCount: register.ticketCount,
  });

  const isDesktop = width > 1024;

  // Purchase Error Message
  let purchaseErrorMsg = '';
  // @ts-ignore
  const errData = mutation.error?.response?.data;
  if (errData?.status === 'invalid') {
    if (errData?.errors?.declined) {
      purchaseErrorMsg = errData.errors.declined;
    } else if (errData?.errors?.ticketCount) {
      purchaseErrorMsg = errData?.errors?.ticketCount[0];
    } else if (errData?.error?.includes('400')) {
      purchaseErrorMsg = 'The transaction was unsuccessful.';
    }
  }

  // Submit button for ticket purchase
  let submitDisabled: boolean = true;
  let submitText: string = 'Submit';
  if (ticketIsFree) {
    submitDisabled =
      mutation.isLoading ||
      mutation.isError ||
      needsToSelectShirts ||
      needsToSelectMeals;
    submitText = mutation.isLoading
      ? 'Registering...'
      : `Register ${register.ticketCount} ticket(s)`;
  } else {
    submitDisabled =
      mutation.isLoading ||
      mutation.isError ||
      needsToSelectShirts ||
      needsToSelectMeals ||
      !billing ||
      !register.customerPaymentProfileId;
    submitText = mutation.isLoading
      ? 'Purchasing...'
      : `Confirm Purchase ${toCurrency({ value: payTodayMax })}`;
  }

  const steps: { label: string; content: (index: number) => JSX.Element }[] = [
    {
      label: 'Select Ticket',
      content: (index) => {
        const nextDisabled: boolean =
          !register.TicketTypeId ||
          register.ticketCount === '0' ||
          (needPurchaseInfo && !isEmail(register.OwnerEmail)) ||
          (needPurchaseInfo && !register.OwnerFirstName) ||
          (needPurchaseInfo && !register.OwnerLastName) ||
          !isEmail(assignment.DisplayEmail) ||
          !assignment.DisplayFirstName ||
          !assignment.DisplayLastName;

        return (
          <div>
            <Stack spacing={1}>
              <RegisterTicketSelect
                data={props.data}
                ticket={ticket}
                onSelect={(newTicket) => {
                  handleScroll();
                  if (newTicket) {
                    const { TicketTypeId } = newTicket;
                    // Update selected ticket
                    setRegister((currentState) => ({
                      ...currentState,
                      TicketTypeId,
                    }));
                    setTicket(newTicket);
                  } else {
                    setRegister((currentState) => ({
                      ...currentState,
                      TicketTypeId: 0,
                    }));
                    setTicket(undefined);
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={needPurchaseInfo}
                    onChange={(event, checked) => {
                      setNeedPurchaseInfo(checked);
                      // Reset ticket Owner
                      setRegister((currentState) => ({
                        ...currentState,
                        OwnerFirstName: '',
                        OwnerLastName: '',
                        OwnerEmail: '',
                      }));
                    }}
                  />
                }
                label="Are you purchasing this ticket for someone else?"
                slotProps={{
                  typography: {
                    fontSize: 14,
                  },
                }}
              />

              {needPurchaseInfo ? (
                <Box>
                  <Typography variant="h6" component="div" gutterBottom>
                    Purchaser Info
                  </Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        id="guest-OwnerFirstName"
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="First Name"
                        name="OwnerFirstName"
                        value={register.OwnerFirstName}
                        onChange={(event) => {
                          const { name, value } = event.target;
                          setRegister((currentState) => ({
                            ...currentState,
                            [name]: value,
                          }));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        id="guest-OwnerLastName"
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Last Name"
                        name="OwnerLastName"
                        value={register.OwnerLastName}
                        onChange={(event) => {
                          const { name, value } = event.target;
                          setRegister((currentState) => ({
                            ...currentState,
                            [name]: value,
                          }));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        id="guest-OwnerEmail"
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Email Address"
                        name="OwnerEmail"
                        type="email"
                        value={register.OwnerEmail}
                        onChange={(event) => {
                          const { name, value } = event.target;
                          setRegister((currentState) => ({
                            ...currentState,
                            [name]: value,
                          }));
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : null}

              <Box>
                <Typography variant="h6" component="div" gutterBottom>
                  Attendee Info
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel id="register-guest-type-label">
                        Registration Type
                      </InputLabel>
                      <Select
                        labelId="register-guest-type-label"
                        id="register-guest-type"
                        value={assignment.AttendeeTypeId}
                        label="Registration Type"
                        onChange={(event) => {
                          const registrationType = event.target.value as string;
                          setAssignment((currentState) => ({
                            ...currentState,
                            AttendeeTypeId: registrationType,
                          }));
                        }}
                      >
                        {['Guest', 'Carrier', 'Vendor'].map((type) => {
                          return (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      id="guest-DisplayFirstName"
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="First Name"
                      name="DisplayFirstName"
                      value={assignment.DisplayFirstName}
                      onChange={(event) => {
                        const { name, value } = event.target;
                        setAssignment((currentState) => ({
                          ...currentState,
                          [name]: value,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      id="guest-DisplayLastName"
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Last Name"
                      name="DisplayLastName"
                      value={assignment.DisplayLastName}
                      onChange={(event) => {
                        const { name, value } = event.target;
                        setAssignment((currentState) => ({
                          ...currentState,
                          [name]: value,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      id="guest-DisplayEmail"
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Email Address"
                      name="DisplayEmail"
                      type="email"
                      value={assignment.DisplayEmail}
                      onChange={(event) => {
                        const { name, value } = event.target;
                        setAssignment((currentState) => ({
                          ...currentState,
                          [name]: value,
                        }));
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  disabled={nextDisabled}
                  onClick={() => {
                    setActiveStep(index + 1);
                    handleScroll();
                  }}
                >
                  Next
                </Button>
              </div>
            </Stack>
          </div>
        );
      },
    },
    {
      label: 'Review Terms',
      content: (index) => {
        const nextDisabled: boolean = !hasAgreed;

        return (
          <div>
            <RegisterTermsAccept
              data={props.data}
              accepted={hasAgreed}
              onChange={(checked) => setHasAgreed(checked)}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(index - 1);
                  handleScroll();
                }}
              >
                Back
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={nextDisabled}
                onClick={() => {
                  setActiveStep(index + 1);
                  handleScroll();
                }}
              >
                Next
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      label: 'Complete Purchase',
      content: (index) => {
        return (
          <div>
            <Stack spacing={1}>
              <Typography variant="h6" component="div" gutterBottom>
                Total
              </Typography>

              <AnimateIn to="bottom">
                <div>
                  {ticket ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ fontSize: 14 }}>
                        {register.ticketCount} x {ticket.Title} at{' '}
                        {toCurrency({ value: ticket.Price })}
                      </div>

                      <div
                        style={{
                          flex: 1,
                          height: 1,
                          backgroundColor: '#00000033',
                          marginLeft: 10,
                          marginRight: 10,
                        }}
                      />

                      <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                        {toCurrency({ value: payTodayMax })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </AnimateIn>

              <RegisterSelections
                ticket={ticket}
                ticketCount={Number(register.ticketCount || 0)}
                shirtSizes={shirtSizes}
                onChangeShirt={(itemKey, newShirt) => {
                  setShirtSizes((currentState) => {
                    return {
                      ...currentState,
                      [itemKey]: newShirt,
                    };
                  });
                }}
                mealTypes={mealTypes}
                onChangeMeal={(itemKey, newMeal) => {
                  setMealTypes((currentState) => {
                    return {
                      ...currentState,
                      [itemKey]: newMeal,
                    };
                  });
                }}
              />

              {ticketIsFree ? null : (
                <React.Fragment>
                  <Typography variant="h6" component="div" gutterBottom>
                    Payment
                  </Typography>

                  <Stack
                    spacing={1}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems="flex-start"
                  >
                    <div style={{ flex: 1, width: '100%' }}>
                      <RegisterPaymentAdd
                        email={register.OwnerEmail || assignment.DisplayEmail}
                        onUpdateBilling={(newBilling, newProfileId) => {
                          if (
                            newBilling &&
                            newBilling.customerPaymentProfiles.length
                          ) {
                            setBilling(newBilling);
                            const { customerPaymentProfileId } =
                              newBilling.customerPaymentProfiles[0];
                            setRegister((currentState) => ({
                              ...currentState,
                              customerProfileId:
                                newBilling.customerProfileId || '',
                              customerPaymentProfileId:
                                customerPaymentProfileId || '',
                            }));
                          }
                        }}
                      />
                    </div>

                    {paymentProfile ? (
                      <div style={{ flex: 1, width: '100%' }}>
                        <div
                          style={{
                            position: 'relative',
                            backgroundColor: '#fff',
                            border: '1px solid #4a4a4a88',
                            display: 'flex',
                            borderRadius: 20,
                            boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgb(239, 239, 239)',
                              borderRadius: '20px 0 0 20px',
                              borderRight: '1px solid #00000044',
                            }}
                          >
                            <div style={{ textAlign: 'center' }}>
                              <CardIcon
                                cardType={paymentProfile.cardType || ''}
                                size="3x"
                              />
                            </div>
                          </div>

                          <Box
                            sx={{
                              flex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                            }}
                          >
                            <div>
                              <strong
                                style={{
                                  fontSize: 14,
                                  fontFamily: 'Oswald',
                                  letterSpacing: 1.1,
                                }}
                              >
                                {paymentProfile.cardType?.toUpperCase()}
                              </strong>
                              <p>
                                {paymentProfile.billToFirstName}{' '}
                                {paymentProfile.billToLastName}
                              </p>
                              <div>
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                  }}
                                >
                                  **** **** ****{' '}
                                  {paymentProfile.cardNumber?.replaceAll(
                                    'X',
                                    '',
                                  )}
                                </span>
                              </div>
                              <div style={{ marginTop: 10 }}>
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    fontSize: 14,
                                  }}
                                >
                                  EXP: {paymentProfile.expirationDate}
                                </span>
                              </div>
                            </div>
                          </Box>
                        </div>
                      </div>
                    ) : null}
                  </Stack>
                </React.Fragment>
              )}

              {mutation.isError ? (
                <Alert severity="error" onClose={() => mutation.reset()}>
                  <AlertTitle>An error has occurred</AlertTitle>
                  {purchaseErrorMsg ||
                    getErrorMessage({
                      error: mutation.error,
                      fallback: 'Please try again.',
                    })}
                </Alert>
              ) : null}

              <Stack
                spacing={1}
                direction="row"
                alignItems="flex-end"
                justifyContent="space-between"
              >
                <Button
                  disabled={mutation.isLoading}
                  variant="outlined"
                  onClick={() => {
                    setActiveStep(index - 1);
                    handleScroll();

                    // Reset shirt sizes and meal types
                    setShirtSizes({});
                    setMealTypes({});
                  }}
                >
                  Back
                </Button>

                <Button
                  color="secondary"
                  variant="contained"
                  size="large"
                  disabled={submitDisabled}
                  onClick={() => mutation.mutate()}
                  startIcon={
                    mutation.isLoading ? <CircularProgress size={20} /> : null
                  }
                >
                  {submitText}
                </Button>
              </Stack>
            </Stack>
          </div>
        );
      },
    },
  ];

  if (mutation.isSuccess) {
    return (
      <RegisterConfirmation
        data={props.data}
        billing={billing}
        paymentProfileId={register.customerPaymentProfileId}
        ticketData={ticket}
        ticketCount={register.ticketCount}
        ticketsPurchased={mutation.data}
        OwnerEmail={register.OwnerEmail}
      />
    );
  } else {
    return (
      <div style={{ backgroundColor: '#fff' }}>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
          <DialogContent>
            <DialogTitle>
              <IconButton
                size="small"
                style={{ position: 'absolute', top: 10, right: 10 }}
                onClick={handleClose}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <div
              style={{
                textAlign: 'center',
                fontFamily: 'Roboto',
                fontSize: 14,
              }}
            >
              You are checking out as a <strong>guest</strong>.<br />
              If you are an Agent make sure to log in.
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleSignIn}
            >
              Login
            </Button>
          </DialogActions>
        </Dialog>

        <Stepper
          activeStep={activeStep}
          orientation={isDesktop ? 'horizontal' : 'vertical'}
          sx={{ p: 1 }}
        >
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              {isDesktop ? null : (
                <StepContent>{step.content(index)}</StepContent>
              )}
            </Step>
          ))}
        </Stepper>

        {isDesktop ? (
          <Box sx={{ backgroundColor: '#fff', p: 2 }}>
            {steps.map((step, index) => {
              if (activeStep === index) {
                return <div key={step.label}>{step.content(index)}</div>;
              } else {
                return null;
              }
            })}
          </Box>
        ) : null}
      </div>
    );
  }
}
